import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircleHeart, Sparkles, Flame, Clock } from "lucide-react";
import { JP } from "@/components/JP";
import { Mascot } from "@/components/Mascot";
import { ProgressRing } from "@/components/ProgressRing";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { LiveClassCard } from "@/components/LiveClassCard";
import { TrendChart, toChartRows } from "@/pages/AssessmentHistory";
import { ExamPlanCard } from "@/components/ExamPlanCard";
import { usePrefs } from "@/context/PrefsContext";
import { api } from "@/lib/api";

function Card({ children, className = "", ...rest }) {
  return <div className={`soft-card p-5 sm:p-6 ${className}`} {...rest}>{children}</div>;
}

function Stat({ label, value, sub, icon: I, testid, children }) {
  return (
    <Card className="!p-3 sm:!p-5" data-testid={testid}>
      <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground sm:text-[11px]">{I && <I className="hidden h-3.5 w-3.5 sm:block" />}<span className="truncate">{label}</span></div>
      <div className="mt-2 flex flex-wrap items-end gap-x-2 gap-y-0.5"><span className="font-jpserif text-2xl leading-none sm:text-3xl">{value}</span>{sub && <span className="pb-0.5 text-[11px] text-muted-foreground sm:text-xs">{sub}</span>}</div>
      {children}
    </Card>
  );
}

export default function Dashboard() {
  const { t, area, lang } = usePrefs();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-sm text-muted-foreground" data-testid="dashboard-loading">{t("loading")}</p>;

  const { user, weekly, latest_assessment: la, next_module: nm, next_reason, streak, mascot, study_level, today, onboarding } = d;
  const weakArea = next_reason?.startsWith("weak:") ? next_reason.slice(5) : null;
  const todayText = today.minutes_left > 0 ? t("todayLeft").replace("{n}", today.minutes_left) : t("todayDone");

  return (
    <div className="space-y-5 fade-up" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("hello")}</p>
          <h1 className="mt-1 font-jpserif text-3xl sm:text-4xl" data-testid="dashboard-greeting">{user.name}</h1>
        </div>
        {mascot && <Mascot m={mascot} size={72} testid="dashboard-mascot" className="soft-card px-4 py-2.5" />}
      </div>

      <OnboardingChecklist ob={onboarding} />
      <LiveClassCard />

      <Card className="border-primary/20 bg-gradient-to-br from-card to-primary/[0.05] !p-6 sm:!p-8" data-testid="continue-card">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 w-full sm:flex-1">
            <p className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">{t("continueLearn")}{study_level && <span data-testid="study-level-badge" className="whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">{la ? t("startLevelRec") : t("startLevelTarget")} {study_level}</span>}</p>
            {nm ? (
              <>
                <JP text={nm.title_jp} as="h2" className="mt-3 text-2xl font-medium sm:text-3xl" />
                <p className="mt-1 text-sm text-muted-foreground">{nm.code ? `${nm.code} · ` : ""}{nm.title_id} · {nm.level} · {nm.duration_min} {t("min")}</p>
                <Link data-testid="continue-learn-button" to={`/modules/${nm.id}`} className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground sm:w-auto shadow-[0_10px_30px_-12px_hsl(var(--primary)/0.6)] transition-transform hover:-translate-y-0.5">{t("continueLearn")}<ArrowRight className="h-4 w-4" /></Link>
              </>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">{t("empty")}</p>
            )}
          </div>
          <div className="flex w-full shrink-0 items-center gap-3 rounded-2xl bg-card/70 px-4 py-3 sm:w-auto" data-testid="today-progress">
            <ProgressRing pct={today.pct} size={56} stroke={5} testid="today-ring"><Clock className="h-4 w-4 text-muted-foreground" /></ProgressRing>
            <div><p className="text-sm font-semibold" data-testid="today-left">{todayText}</p><p className="text-xs text-muted-foreground">{today.minutes_done}/{today.minutes_goal} {t("todayMin")}</p></div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-3 gap-3 [&>*]:min-w-0" data-testid="stats-row">
        <Stat label={t("targetLevel")} value={<span data-testid="target-level-value">{user.target_level}</span>} sub="JLPT" testid="target-level-card">
          {user.institution_name && <span className="mt-3 inline-block max-w-full truncate rounded-full bg-muted px-2.5 py-0.5 text-[11px]" data-testid="institution-badge">{user.institution_name}</span>}
        </Stat>
        <Stat label={t("streak")} icon={Flame} value={<span data-testid="streak-current" className={streak.today_done ? "pop-in text-primary" : ""}>{streak.current}</span>} sub={<><span>{t("days")}</span><span className="hidden sm:inline"> · {t("best")} {streak.longest}</span></>} testid="streak-card">
          <div className="mt-3 flex items-center gap-1" data-testid="streak-last7">
            {streak.last7.map((x, i) => <span key={x.date} title={x.date} data-testid={`streak-day-${i}`} data-active={x.active} className={`h-2 flex-1 rounded-full transition-colors ${x.active ? "bg-primary" : "bg-muted"}`} />)}
          </div>
        </Stat>
        <Stat label={t("weekProgress")} value={<span data-testid="weekly-done">{weekly.done}</span>} sub={`/${weekly.goal} ${t("activities")}`} testid="weekly-progress-card">
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted"><div data-testid="weekly-progress-bar" className="h-full rounded-full bg-sage transition-[width] duration-700" style={{ width: `${weekly.pct}%` }} /></div>
          {d.plan?.total ? <p className="mt-2 truncate text-[11px] text-muted-foreground" data-testid="plan-progress">Plan {d.plan.id}: {d.plan.done}/{d.plan.total}</p> : null}
        </Stat>
      </div>

      <div className="grid gap-4 md:grid-cols-12 [&>*]:min-w-0">
        <Card className="md:col-span-5" data-testid="assessment-summary-card">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("assessmentSummary")}</p>
          {la ? (
            <>
              <div className="mt-3 flex items-end gap-2"><span className="font-jpserif text-4xl leading-none" data-testid="assessment-summary-score">{la.score}</span><span className="pb-0.5 text-sm text-muted-foreground">/ {la.total} · {la.pct}%</span></div>
              <p className="mt-2 text-sm">{t("recommendedLevel")}: <span className="font-semibold">{la.recommended_level}</span></p>
              <div className="mt-4 flex flex-wrap gap-2">{la.breakdown.map((b) => <span key={b.area} className={`rounded-full px-2.5 py-1 text-xs ${b.pct < 70 ? "bg-primary/10 text-primary" : "bg-sage-soft text-sage"}`}>{area(b.area)} {b.pct}%</span>)}</div>
              <div className="mt-4 flex flex-wrap gap-x-4 text-xs font-medium"><Link data-testid="assessment-retake-link" to="/assessment" className="inline-flex min-h-[40px] items-center underline-offset-4 hover:underline">{t("retakeAssessment")}</Link><Link data-testid="assessment-history-link" to="/assessment/history" className="inline-flex min-h-[40px] items-center underline-offset-4 hover:underline">{t("historyTitle")} ({d.assessment_count})</Link></div>
            </>
          ) : (
            <>
              <p className="mt-3 text-sm text-muted-foreground">{t("noAssessment")}</p>
              <Link data-testid="start-assessment-button" to="/assessment" className="mt-5 inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition-transform hover:-translate-y-0.5">{t("startAssessment")}<ArrowRight className="h-4 w-4" /></Link>
            </>
          )}
        </Card>

        <Card className="md:col-span-7" data-testid="recommendation-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground"><Sparkles className="h-3.5 w-3.5" />{t("todayRec")}</div>
          <p className="mt-3 text-base leading-relaxed">
            {weakArea
              ? (lang === "jp" ? `診断結果より、${area(weakArea)}を重点的に。` : `Berdasarkan assessment, ${t("weakIn").toLowerCase()} ${area(weakArea)} dulu.`)
              : la
                ? (lang === "jp" ? "順調です。次の教材に進みましょう。" : "Progresmu baik. Lanjutkan ke modul berikutnya.")
                : (lang === "jp" ? "まず診断を受けて、学習パスを決めましょう。" : "Mulai dari assessment agar jalur belajarmu jelas.")}
          </p>
          {d.recent_tests?.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="recent-tests">
              {d.recent_tests.map((r) => (
                <Link key={r.id} to={`/test/result/${r.id}`} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm transition-colors hover:bg-muted"><span className="truncate">{r.module_title}</span><span className={`ml-3 shrink-0 text-xs font-semibold ${r.passed ? "text-sage" : "text-primary"}`}>{r.score}/{r.total}</span></Link>
              ))}
            </div>
          )}
        </Card>

        {d.assessment_trend?.length > 0 && (
          <Card className="md:col-span-12" data-testid="assessment-trend-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><p className="text-xs uppercase tracking-wide text-muted-foreground">{t("trendTitle")}</p><p className="mt-1 text-sm text-muted-foreground">{t("trendDashDesc")}</p></div>
              <Link data-testid="trend-history-link" to="/assessment/history" className="inline-flex min-h-[40px] items-center gap-1 text-xs font-medium underline-offset-4 hover:underline">{t("historyTitle")}<ArrowRight className="h-3 w-3" /></Link>
            </div>
            <div className="mt-3 min-w-0 overflow-hidden"><TrendChart rows={toChartRows(d.assessment_trend.map((r, i) => ({ ...r, attempt: d.assessment_count - d.assessment_trend.length + i + 1 })))} height={190} compact /></div>
          </Card>
        )}

        <ExamPlanCard className="md:col-span-12" />

        <Card className="md:col-span-12 flex flex-wrap items-center justify-between gap-4" data-testid="care-card">
          <div><div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground"><MessageCircleHeart className="h-3.5 w-3.5" />{t("care")}</div><p className="mt-2 text-sm text-muted-foreground">{t("b3d")}</p></div>
          <Link data-testid="care-center-button" to="/care-center" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-accent">{t("careCta")}</Link>
        </Card>
      </div>
    </div>
  );
}
